<template>
  <div class="member-badge-list-parts" :class="{ 'member-badge-list-parts--hidden': !visibleList }">
    <div
      class="member-badge-list-parts__content"
      ref="MemberBadgeListPartsContent"
      @scroll="checkCarouselEnabled"
    >
      <div class="member-badge-list-parts__list">
        <member-badge-item-parts
          v-for="(badge, idx) in badgeList"
          :class="`member-badge-list-parts__item member-badge-list-parts__item--${idx}`"
          :key="`memberBadgeItem${idx}`"
          :badge="badge"
        />
      </div>
    </div>
    <button
      v-if="enabledListCarousel"
      class="member-badge-list-parts__carousel member-badge-list-parts__carousel-prev"
      :class="{ 'member-badge-list-parts__carousel--active': enabledCarouselPrev }"
      @click="onclickCarousel('prev')"
    >
      <img :src="require('@/assets/img/icon/icon_chevron_left_24.svg')" alt="<" />
    </button>

    <button
      v-if="enabledListCarousel"
      class="member-badge-list-parts__carousel member-badge-list-parts__carousel-next"
      :class="{ 'member-badge-list-parts__carousel--active': enabledCarouselNext }"
      @click="onclickCarousel('next')"
    >
      <img :src="require('@/assets/img/icon/icon_chevron_right_24.svg')" alt=">" />
    </button>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, ref } from '@vue/composition-api'
import MemberBadgeItemParts, {
  BadgeData,
} from '@/components/MypageTopPage/MembershipCardPane/parts/MemberBadgeItemParts.vue'

/**
 * マイページ: 会員証 バッヂリストパーツ
 */
export default defineComponent({
  name: 'MemberBadgeListParts',
  components: { MemberBadgeItemParts },
  props: {
    badgeList: {
      type: Array as PropType<BadgeData[]>,
      default: () => [],
    },
    enabledListCarousel: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const visibleList = ref<boolean>(false)
    const enabledCarouselPrev = ref<boolean>(false)
    const enabledCarouselNext = ref<boolean>(false)

    const listItemWidths = ref<number[]>([])

    const setListItemWidths = () => {
      const memberBadgeList: HTMLElement | null = document.querySelector(
        '.member-badge-list-parts__list',
      )
      const listItems: NodeList = document.querySelectorAll('.member-badge-list-parts__item')
      let listWidth = 0

      if (listItems.length > 0) {
        listItems.forEach((item) => {
          const itemWidth = (item as HTMLElement).offsetWidth
          listItemWidths.value.push(itemWidth)

          listWidth += itemWidth
        })
        listWidth += listItems.length * 6 + 4
      }

      if (memberBadgeList !== null) {
        memberBadgeList.style.width = `${listWidth}px`
      }
    }

    return {
      visibleList,
      listItemWidths,
      setListItemWidths,
      enabledCarouselPrev,
      enabledCarouselNext,
    }
  },
  mounted() {
    if (this.enabledListCarousel) {
      // 読込時に表示判定
      setTimeout(() => {
        this.checkCarouselEnabled()
      }, 500)

      // リサイズ時に表示判定
      window.addEventListener('resize', this.checkCarouselEnabled)
    }
    setTimeout(() => {
      this.setListItemWidths()

      this.visibleList = true
    }, 500)
  },
  beforeDestroy() {
    if (this.enabledListCarousel) {
      window.removeEventListener('resize', this.checkCarouselEnabled)
    }
  },
  methods: {
    /**
     * ページャー有効化判定
     */
    checkCarouselEnabled() {
      if (this.$refs.MemberBadgeListPartsContent) {
        const LIST_PADDING = 6
        const { scrollLeft, clientWidth, scrollWidth } = this.$refs
          .MemberBadgeListPartsContent as HTMLElement

        this.enabledCarouselPrev = scrollLeft > LIST_PADDING
        this.enabledCarouselNext = scrollLeft < scrollWidth - clientWidth - LIST_PADDING
      }
    },
    /**
     * カルーセルクリックアクション
     * @param type
     */
    onclickCarousel(type: 'prev' | 'next'): void {
      const scroll = {
        value: 0,
        setValue: 0,
        returnFlag: false,
      }

      const badgeList = this.$refs.MemberBadgeListPartsContent as HTMLElement
      const { scrollLeft } = badgeList

      const typeDiff = type === 'next' ? -36 : 36

      this.listItemWidths.forEach((width, idx) => {
        scroll.value += width + 6

        if (scrollLeft <= scroll.value + typeDiff && !scroll.returnFlag) {
          scroll.setValue = scroll.value
          scroll.returnFlag = true

          if (type === 'prev') {
            scroll.setValue -= this.listItemWidths[idx] - 30
            scroll.setValue -= 36
          } else if (type === 'next') {
            scroll.setValue += this.listItemWidths[idx + 1] - 66
          }
        }
      })

      badgeList.scroll({
        left: scroll.setValue,
        behavior: 'smooth',
      })
    },
  },
})
</script>

<style lang="scss" scoped>
.member-badge-list-parts {
  position: relative;
  width: 100%;
  margin-bottom: -4px;

  &--hidden {
    visibility: hidden;
  }

  &__content {
    width: 100%;
    padding: 0 0 4px 10px;
    overflow-y: hidden;
    overflow-x: auto;
  }

  &__list {
    display: flex;
    flex-flow: row nowrap;
    align-items: flex-start;
    justify-content: flex-start;
    gap: 6px;
    width: 500vw;
    margin: 0 auto;

    &::after {
      display: block;
      content: '';
      min-width: 4px;
      height: 1px;
    }
  }

  &__carousel {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    position: absolute;
    top: calc(50% - 16px);
    width: 32px;
    height: 32px;
    background: $white;
    border: 1px solid rgba(0, 0, 0, 0.1);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    border-radius: 50%;
    z-index: 4;
    transition: opacity ease 0.2s;
    opacity: 0;
    pointer-events: none;
    cursor: pointer;

    img {
      pointer-events: none;
      width: 18px;
    }

    &-prev {
      left: 10px;
    }

    &-next {
      right: 10px;
    }

    &--active {
      opacity: 1;
      pointer-events: auto;
    }
  }
}
</style>
